import { FC, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, LinearProgress, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useDocumentStatusSummary, useProfileSummary } from '@features/tenant-profile-hub';
import { TenantProfileDocumentStatus } from '@libs/constants';
import { useTrans } from '@libs/i18n';
import {
  ArrowRightIcon,
  BuildingsIcon,
  EnvelopeSimpleIcon,
  FilesIcon,
  ListChecksIcon,
  StackIcon,
} from '@phosphor-icons/react';

import { DashboardWidget } from './DashboardWidget';

const MiniBar: FC<{ pct: number; color: string }> = observer(({ pct, color }) => {
  const theme = useTheme();
  return (
    <LinearProgress
      variant="determinate"
      value={Math.min(100, Math.max(0, pct))}
      sx={{
        height: 5,
        borderRadius: 999,
        backgroundColor: theme.palette.grey[300],
        '& .MuiLinearProgress-bar': { backgroundColor: color, borderRadius: 999 },
      }}
    />
  );
});

const OrgStat: FC<{ icon: ReactNode; iconColor?: string; label: string; accent?: boolean; children: ReactNode }> =
  observer(({ icon, iconColor, label, accent, children }) => {
    const theme = useTheme();
    return (
      <Box
        sx={{
          border: `1px solid ${accent ? theme.palette.yellow[300] : theme.palette.grey[300]}`,
          backgroundColor: accent ? theme.palette.yellow[100] : theme.palette.common.white,
          borderRadius: 3,
          p: 1.75,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.125,
          minWidth: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875, color: iconColor ?? theme.palette.text.tertiary }}>
          {icon}
          <Typography sx={{ fontSize: 11.5, fontWeight: 500, color: 'text.secondary' }} noWrap>
            {label}
          </Typography>
        </Box>
        {children}
      </Box>
    );
  });

/**
 * Organization-profile health: completion %, categories approved, documents
 * uploaded (with approved/processing split), fields filled, and reviews
 * pending — sourced from the tenant-profile summary + document status summary.
 */
export const OrgProfileWidget: FC = observer(() => {
  const theme = useTheme();
  const { t } = useTrans();
  const navigate = useNavigate();

  const { data: summary } = useProfileSummary();
  const { data: docSummary } = useDocumentStatusSummary();

  const progressPct = summary?.progressPct ?? 0;
  const fieldsFilled = summary?.fieldsFilled ?? 0;
  const totalFields = summary?.totalFields ?? 0;
  const remaining = Math.max(0, totalFields - fieldsFilled);
  const fieldsPct = totalFields === 0 ? 0 : Math.round((fieldsFilled / totalFields) * 100);

  const byStatus = docSummary?.byStatus;
  const approvedDocs = byStatus?.[TenantProfileDocumentStatus.DONE] ?? 0;
  const processingDocs =
    (byStatus?.[TenantProfileDocumentStatus.CLASSIFYING] ?? 0) +
    (byStatus?.[TenantProfileDocumentStatus.EXTRACTING] ?? 0);

  return (
    <DashboardWidget
      icon={<BuildingsIcon size={19} />}
      iconColor={theme.palette.text.secondary}
      title={t('app.dashboard.orgProfile.title')}
      subtitle={t('app.dashboard.orgProfile.subtitle')}
      right={
        <Button
          variant="secondary"
          endIcon={<ArrowRightIcon size={12} />}
          onClick={() => navigate('/tenant-profile')}
          sx={{ textTransform: 'none', color: 'text.primary' }}
        >
          {t('app.dashboard.orgProfile.open')}
        </Button>
      }
    >
      <Box
        sx={{
          flex: 1,
          p: 2.5,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1.5,
          alignContent: 'start',
        }}
      >
        <OrgStat icon={<ListChecksIcon size={14} />} label={t('app.dashboard.orgProfile.completion')}>
          <Typography sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1, color: theme.palette.blue[700] }}>
            {progressPct}%
          </Typography>
          <MiniBar pct={progressPct} color={theme.palette.blue[700]} />
          <Typography sx={{ fontSize: 11, color: 'text.tertiary' }}>
            {t('app.dashboard.orgProfile.categoriesApproved', {
              approved: summary?.categoriesApproved ?? 0,
              total: summary?.categoriesTotal ?? 0,
            })}
          </Typography>
        </OrgStat>

        <OrgStat icon={<FilesIcon size={14} />} label={t('app.dashboard.orgProfile.documents')}>
          <Typography sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1 }}>
            {summary?.documentsUploaded ?? 0}
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.tertiary' }}>
            {t('app.dashboard.orgProfile.documentsBreakdown', {
              approved: approvedDocs,
              processing: processingDocs,
            })}
          </Typography>
        </OrgStat>

        <OrgStat icon={<StackIcon size={14} />} label={t('app.dashboard.orgProfile.fields')}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.25 }}>
            <Typography sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1 }}>{fieldsFilled}</Typography>
            <Typography sx={{ fontSize: 15, color: 'text.tertiary' }}>/{totalFields}</Typography>
          </Box>
          <MiniBar pct={fieldsPct} color={theme.palette.text.secondary} />
          <Typography sx={{ fontSize: 11, color: 'text.tertiary' }}>
            {t('app.dashboard.orgProfile.fieldsRemaining', { count: remaining })}
          </Typography>
        </OrgStat>

        <OrgStat
          icon={<EnvelopeSimpleIcon size={14} />}
          iconColor={theme.palette.yellow[700]}
          label={t('app.dashboard.orgProfile.reviews')}
          accent
        >
          <Typography
            sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1, color: theme.palette.yellow[700] }}
          >
            {summary?.pendingReviewCount ?? 0}
          </Typography>
          <Typography
            sx={{ fontSize: 11, color: 'text.tertiary', cursor: 'pointer' }}
            onClick={() => navigate('/tenant-profile/document-hub')}
          >
            {t('app.dashboard.orgProfile.reviewsHint')}
          </Typography>
        </OrgStat>
      </Box>
    </DashboardWidget>
  );
});
